import { MouseEvent, useContext, useState } from 'react';

import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import {
  selectViewAllModal,
  setViewAllModal,
  useDeleteScenarioMutation,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectCurrentScenarioId,
} from 'state/workflowSlice';

import { ACTIVE_STATUSES, WorkflowType } from 'appConstants';
import { ProcessStatus, Scenario } from 'appTypes';
import { ConfirmDeletePopup } from 'features/common/ConfirmDeletePopup';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import {
  ExportType,
  downloadMapScreenshot,
  useExportFilename,
} from 'features/common/exportUtils';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { MapContext } from 'features/map/MapContext';
import { useMenu } from 'features/workflow_routemonitoring/hooks/useMenu';

import { useScenarioUI } from '../hooks/useScenarioUI';

import styles from '../../common/common.module.css';

interface IScenarioCardMenu {
  scenario: Scenario;
  scenarioStatus: ProcessStatus;
}

export default function ScenarioCardMenu({
  scenario,
  scenarioStatus,
}: IScenarioCardMenu) {
  const dispatch = useAppDispatch();

  const [deleteScenarioId, setDeleteScenarioId] = useState(undefined);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const viewAllModal = useAppSelector(selectViewAllModal);
  const user = useAppSelector(selectUser);
  const userProject = useAppSelector(selectCurrentProjectInfo);

  const { subscription_id, project_slug } = userProject;
  const { subscription } = useSubscription(subscription_id, project_slug);
  const { anchorEl, open, onClose, onOpen } = useMenu();
  const { setScenariosMode, setEditMode, setCopyMode } = useScenarioUI();

  const [deleteScenario] = useDeleteScenarioMutation();
  const { setInitialMode } = useScenarioUI();
  const filename = useExportFilename(ExportType.Map);

  const handleClose = () => {
    if (viewAllModal) dispatch(setViewAllModal(false));
    onClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteScenario({
        project_slug: project_slug as string,
        scenario_id: scenario.scenario_id,
      })
        .unwrap()
        .then(() => {
          if (scenario.scenario_id === currentScenarioId) setInitialMode();
        });
    } catch (e) {
      console.error('Scenario delete failed', e);
    }
    setDeleteScenarioId(undefined);
  };

  const onDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setDeleteScenarioId(scenario.scenario_id);
    onClose();
  };

  const onLink = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    try {
      // another way:
      //   const baseState = deepClone(getState().workflow);
      //   getWorkflowState(baseState).currentScenarioId = scenario.id
      //   const searchParams = getSearchParamsForState(baseState);
      //   const link = pathForSearchParams(searchParams);
      const params = new URLSearchParams({
        project: project_slug,
        workflow: WorkflowType.Detouring,
        scenario: scenario.scenario_id,
      });

      const link = `${window.location.origin}/app?${params.toString()}`;
      await navigator.clipboard.writeText(link);
    } catch (err) {
      console.error('Clipboard copy failed: ', err);
    }
    onClose();
  };

  const onCopy = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCopyMode(scenario);
    handleClose();
  };

  const onView = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setScenariosMode(scenario);
    handleClose();
  };

  const onEdit = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setEditMode(scenario);
    handleClose();
  };
  const { refMap } = useContext(MapContext);

  const onExport = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    downloadMapScreenshot(refMap?.current, filename);
    handleClose();
  };

  const isProcessing = ACTIVE_STATUSES.includes(scenarioStatus);

  const isOwner = scenario.created_by === user.username;
  const isSubscriptionAdmin = !!subscription?.user_is_sub_admin;
  const canDelete = isOwner || isSubscriptionAdmin;
  const showViewOption =
    currentScenarioId !== scenario.scenario_id || viewAllModal;
  return (
    <>
      <IconButton
        size="small"
        aria-label="More options"
        aria-controls={open ? 'scenario-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <FontAwesomeIcon
          name="ellipsis-vertical"
          size="0.875rem"
          sx={{ color: 'white' }}
        />
      </IconButton>
      <Menu
        className={styles.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        {showViewOption && (
          <MenuItem onClick={onView} className={styles.menu_item}>
            <ListItemIcon aria-label="view scenario">
              <FontAwesomeIcon name="square-arrow-up-right" size="0.75rem" />
            </ListItemIcon>
            <Typography variant="caption">View Scenario</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={onLink} className={styles.menu_item}>
          <ListItemIcon aria-label="copy link">
            <FontAwesomeIcon name="link" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Copy link</Typography>
        </MenuItem>
        <MenuItem onClick={onExport} className={styles.menu_item}>
          <ListItemIcon aria-label="export">
            <FontAwesomeIcon name="cloud-arrow-down" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Export Map</Typography>
        </MenuItem>
        {isOwner && (
          <MenuItem
            onClick={onEdit}
            className={styles.menu_item}
            disabled={isProcessing}
          >
            <ListItemIcon aria-label="edit">
              <FontAwesomeIcon name="pen" size="0.75rem" />
            </ListItemIcon>
            <Typography variant="caption">Edit</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={onCopy} className={styles.menu_item}>
          <ListItemIcon aria-label="copy">
            <FontAwesomeIcon name="copy" size="0.75rem" />
          </ListItemIcon>
          <Typography variant="caption">Copy</Typography>
        </MenuItem>
        {canDelete && (
          <MenuItem
            onClick={onDelete}
            className={`${styles.menu_item} ${styles.menu_item_delete}`}
          >
            <ListItemIcon aria-label="delete" title="Delete">
              <FontAwesomeIcon name="trash" size="0.75rem" />
            </ListItemIcon>
            <Typography variant="caption">Delete</Typography>
          </MenuItem>
        )}
      </Menu>
      <ConfirmDeletePopup
        objectType="scenario"
        isOpen={Boolean(deleteScenarioId)}
        setIsOpen={() => setDeleteScenarioId(null)}
        onApply={handleConfirmDelete}
        name={scenario.scenario_name ?? ''}
      />
    </>
  );
}
